
import React, { useEffect, useState } from "react";
import {
  FaKey,
  FaLock,
  FaSpinner,
  FaUser,
  FaUserMd,
  FaShieldAlt,
  FaEnvelope,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  resetPasswordRequest,
  clearResetState,
} from "../../redux/slices/passwords/resetPasswordRequest";

import {
  resetBusinessPasswordRequest,
  clearBusinessResetState,
} from "../../redux/slices/passwords/resetBusinessPasswordSlice";

const ResetPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ============================================================
  // STATE
  // ============================================================

  const [userType, setUserType] = useState("");

  const [formData, setFormData] = useState({
    otp: "",
    newPassword: "",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  // ============================================================
  // RESET EMAIL
  // ============================================================

  const email = localStorage.getItem("resetEmail") || "";

  // ============================================================
  // REDUX STATE
  // ============================================================

  const businessState = useSelector(
    (state) => state.resetBusinessPassword || {}
  );

  const userState = useSelector(
    (state) => state.resetPassword || {}
  );

  const activeState =
    userType === "business" ? businessState : userState;

  const loading = Boolean(activeState.loading);
  const message = activeState.message || "";
  const error = activeState.error || "";

  // ============================================================
  // CHECK RESET EMAIL
  // ============================================================

  useEffect(() => {
    if (!email) {
      toast.error("Reset session expired. Please request a new OTP.");

      const timer = setTimeout(() => {
        navigate("/forgot-password", { replace: true });
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [email, navigate]);

  // ============================================================
  // SUCCESS / ERROR HANDLING
  // ============================================================

  useEffect(() => {
    if (!isSubmitted) return;

    if (message) {
      toast.success(message);

      setFormData({
        otp: "",
        newPassword: "",
      });

      setIsSubmitted(false);

      if (userType === "business") {
        dispatch(clearBusinessResetState());
      } else {
        dispatch(clearResetState());
      }

      localStorage.removeItem("resetEmail");

      const timer = setTimeout(() => {
        navigate("/login", { replace: true });
      }, 1200);

      return () => clearTimeout(timer);
    }

    if (error) {
      toast.error(error);
      setIsSubmitted(false);

      if (userType === "business") {
        dispatch(clearBusinessResetState());
      } else {
        dispatch(clearResetState());
      }
    }
  }, [
    message,
    error,
    userType,
    isSubmitted,
    dispatch,
    navigate,
  ]);

  // ============================================================
  // INPUT HANDLER
  // ============================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "otp") {
      const numericValue = value
        .replace(/\D/g, "")
        .slice(0, 6);

      setFormData((prev) => ({
        ...prev,
        otp: numericValue,
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ============================================================
  // SELECT ACCOUNT TYPE
  // ============================================================

  const handleUserTypeSelect = (type) => {
    dispatch(clearResetState());
    dispatch(clearBusinessResetState());

    setFormData({
      otp: "",
      newPassword: "",
    });

    setIsSubmitted(false);
    setUserType(type);
  };

  // ============================================================
  // PASSWORD VALIDATION
  // ============================================================

  const validatePassword = (password) => {
    if (!password) {
      return false;
    }

    return password.length >= 6;
  };

  // ============================================================
  // SUBMIT
  // ============================================================

  const handleSubmit = (e) => {
    e.preventDefault();

    if (loading) {
      return;
    }

    const otp = formData.otp.trim();
    const newPassword = formData.newPassword;

    // ----------------------------------------------------------
    // ACCOUNT TYPE
    // ----------------------------------------------------------

    if (!userType) {
      toast.error("Please select account type");
      return;
    }

    // ----------------------------------------------------------
    // EMAIL
    // ----------------------------------------------------------

    if (!email) {
      toast.error(
        "Reset session expired. Please request a new OTP."
      );
      return;
    }

    // ----------------------------------------------------------
    // OTP
    // ----------------------------------------------------------

    if (!otp) {
      toast.error("Please enter the OTP");
      return;
    }

    if (!/^\d{6}$/.test(otp)) {
      toast.error("OTP must be exactly 6 digits");
      return;
    }

    // ----------------------------------------------------------
    // PASSWORD
    // ----------------------------------------------------------

    if (!newPassword) {
      toast.error("Please enter your new password");
      return;
    }

    if (!validatePassword(newPassword)) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    // ----------------------------------------------------------
    // PAYLOAD
    // ----------------------------------------------------------

    const payload = {
      email,
      otp,
      newPassword,
    };

    console.log("🚀 Reset password request:", {
      email,
      accountType: userType,
    });

    setIsSubmitted(true);

    // ----------------------------------------------------------
    // DISPATCH
    // ----------------------------------------------------------

    if (userType === "business") {
      dispatch(resetBusinessPasswordRequest(payload));
    } else {
      dispatch(resetPasswordRequest(payload));
    }
  };

  // ============================================================
  // CHANGE ACCOUNT TYPE
  // ============================================================

  const handleChangeAccountType = () => {
    if (loading) {
      return;
    }

    dispatch(clearResetState());
    dispatch(clearBusinessResetState());

    setFormData({
      otp: "",
      newPassword: "",
    });

    setIsSubmitted(false);
    setUserType("");
  };

  // ============================================================
  // UI
  // ============================================================

  return (
    <div
      className="
        min-h-screen
        flex
        items-center
        justify-center
        px-4
        py-8
        relative
        overflow-hidden
      "
      style={{
        background:
          "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)",
      }}
    >
      {/* =====================================================
          BACKGROUND DECORATION
      ====================================================== */}

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 h-80 w-80 rounded-full bg-cyan-400/10 blur-3xl" />

        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />

        <div className="absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-sky-400/5 blur-3xl" />
      </div>

      {/* =====================================================
          MAIN CARD
      ====================================================== */}

      <div
        className="
          relative
          z-10
          w-full
          max-w-md
          overflow-hidden
          rounded-3xl
          border
          border-white/20
          bg-white/95
          shadow-2xl
          backdrop-blur-xl
        "
      >
        {/* ===================================================
            HEADER
        ==================================================== */}

        <div
          className="px-7 pb-7 pt-8 text-center text-white"
          style={{
            background:
              "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)",
          }}
        >
          <div
            className="
              mx-auto
              mb-4
              flex
              h-16
              w-16
              items-center
              justify-center
              rounded-2xl
              border
              border-white/20
              bg-white/10
              shadow-lg
              backdrop-blur-md
            "
          >
            <FaLock className="text-2xl text-cyan-300" />
          </div>

          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Reset Password
          </h2>

          <p className="mx-auto mt-2 max-w-xs text-sm leading-6 text-blue-100/80">
            Enter your OTP and create a secure new password.
          </p>
        </div>

        {/* ===================================================
            CONTENT
        ==================================================== */}

        <div className="p-6 sm:p-8">

          {/* =================================================
              STEP 1 — ACCOUNT TYPE TABS
          ================================================== */}

          {!userType ? (
            <div>
              <div className="mb-5 text-center">
                <p className="text-sm font-semibold text-gray-700">
                  Select Account Type
                </p>

                <p className="mt-1 text-xs text-gray-400">
                  Choose the account you want to reset
                </p>
              </div>

              {/* =================================================
                  ACCOUNT TYPE TABS
              ================================================== */}

              <div
                className="
                  grid
                  grid-cols-2
                  gap-2
                  rounded-2xl
                  border
                  border-gray-200
                  bg-gray-100
                  p-1.5
                "
              >
                {/* USER / PATIENT */}

                <button
                  type="button"
                  onClick={() => handleUserTypeSelect("user")}
                  className="
                    group
                    flex
                    min-h-[100px]
                    flex-col
                    items-center
                    justify-center
                    rounded-xl
                    px-3
                    py-4
                    text-center
                    text-gray-600
                    transition-all
                    duration-200
                    hover:bg-white
                    hover:text-gray-800
                    hover:shadow-sm
                  "
                >
                  <div
                    className="
                      mb-2
                      flex
                      h-11
                      w-11
                      items-center
                      justify-center
                      rounded-full
                      bg-sky-100
                      text-sky-600
                      transition-all
                      duration-200
                      group-hover:scale-105
                      group-hover:bg-sky-600
                      group-hover:text-white
                    "
                  >
                    <FaUser />
                  </div>

                  <span className="text-sm font-bold">
                    User / Patient
                  </span>

                  <span className="mt-1 text-[10px] text-gray-400">
                    Personal Account
                  </span>
                </button>

                {/* BUSINESS / DOCTOR */}

                <button
                  type="button"
                  onClick={() =>
                    handleUserTypeSelect("business")
                  }
                  className="
                    group
                    flex
                    min-h-[100px]
                    flex-col
                    items-center
                    justify-center
                    rounded-xl
                    px-3
                    py-4
                    text-center
                    text-gray-600
                    transition-all
                    duration-200
                    hover:bg-white
                    hover:text-gray-800
                    hover:shadow-sm
                  "
                >
                  <div
                    className="
                      mb-2
                      flex
                      h-11
                      w-11
                      items-center
                      justify-center
                      rounded-full
                      bg-sky-100
                      text-sky-600
                      transition-all
                      duration-200
                      group-hover:scale-105
                      group-hover:bg-sky-600
                      group-hover:text-white
                    "
                  >
                    <FaUserMd />
                  </div>

                  <span className="text-sm font-bold">
                    Business / Doctor
                  </span>

                  <span className="mt-1 text-[10px] text-gray-400">
                    Professional Account
                  </span>
                </button>
              </div>

              {/* SECURITY */}

              <div className="mt-6 flex items-center justify-center gap-2">
                <FaShieldAlt className="text-xs text-sky-500" />

                <p className="text-xs text-gray-400">
                  Your account information is securely protected.
                </p>
              </div>
            </div>
          ) : (

            /* ==================================================
               STEP 2 — RESET PASSWORD
            ================================================== */

            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              noValidate
            >
              {/* =================================================
                  SELECTED ACCOUNT TYPE
              ================================================== */}

              <div
                className="
                  flex
                  items-center
                  justify-between
                  rounded-xl
                  border
                  border-sky-100
                  bg-sky-50/70
                  px-4
                  py-3
                "
              >
                <div className="flex items-center gap-3">
                  <div
                    className="
                      flex
                      h-10
                      w-10
                      items-center
                      justify-center
                      rounded-full
                      bg-sky-600
                      text-white
                    "
                  >
                    {userType === "business" ? (
                      <FaUserMd />
                    ) : (
                      <FaUser />
                    )}
                  </div>

                  <div>
                    <p className="text-xs text-gray-400">
                      Account Type
                    </p>

                    <p className="text-sm font-bold text-gray-700">
                      {userType === "business"
                        ? "Business / Doctor"
                        : "User / Patient"}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleChangeAccountType}
                  disabled={loading}
                  className="
                    text-xs
                    font-semibold
                    text-sky-600
                    transition
                    hover:text-blue-700
                    hover:underline
                    disabled:cursor-not-allowed
                    disabled:opacity-50
                  "
                >
                  Change
                </button>
              </div>

              {/* =================================================
                  EMAIL
              ================================================== */}

              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  Email Address
                </label>

                <div className="relative">
                  <FaEnvelope
                    className="
                      pointer-events-none
                      absolute
                      left-4
                      top-1/2
                      -translate-y-1/2
                      text-gray-400
                    "
                  />

                  <input
                    type="email"
                    value={email}
                    readOnly
                    className="
                      w-full
                      rounded-xl
                      border
                      border-gray-200
                      bg-gray-50
                      py-3.5
                      pl-11
                      pr-4
                      text-sm
                      text-gray-500
                      outline-none
                    "
                  />
                </div>
              </div>

              {/* =================================================
                  OTP
              ================================================== */}

              <div>
                <label
                  htmlFor="otp"
                  className="mb-2 block text-sm font-semibold text-gray-700"
                >
                  <FaKey className="mr-1.5 inline text-sky-600" />
                  Verification OTP
                </label>

                <input
                  id="otp"
                  type="text"
                  name="otp"
                  value={formData.otp}
                  onChange={handleChange}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="Enter 6-digit OTP"
                  disabled={loading}
                  className="
                    w-full
                    rounded-xl
                    border
                    border-gray-200
                    bg-gray-50
                    px-4
                    py-3.5
                    text-center
                    text-sm
                    font-semibold
                    tracking-[0.35em]
                    text-gray-800
                    outline-none
                    transition-all
                    duration-200
                    placeholder:font-normal
                    placeholder:tracking-normal
                    placeholder:text-gray-400
                    hover:border-gray-300
                    focus:border-sky-500
                    focus:bg-white
                    focus:ring-4
                    focus:ring-sky-100
                    disabled:cursor-not-allowed
                    disabled:bg-gray-100
                  "
                />

                <p className="mt-2 text-xs text-gray-400">
                  Enter the 6-digit OTP sent to your email.
                </p>
              </div>

              {/* =================================================
                  NEW PASSWORD
              ================================================== */}

              <div>
                <label
                  htmlFor="newPassword"
                  className="mb-2 block text-sm font-semibold text-gray-700"
                >
                  <FaLock className="mr-1.5 inline text-sky-600" />
                  New Password
                </label>

                <input
                  id="newPassword"
                  type="password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                  placeholder="Enter your new password"
                  disabled={loading}
                  className="
                    w-full
                    rounded-xl
                    border
                    border-gray-200
                    bg-gray-50
                    px-4
                    py-3.5
                    text-sm
                    text-gray-800
                    outline-none
                    transition-all
                    duration-200
                    placeholder:text-gray-400
                    hover:border-gray-300
                    focus:border-sky-500
                    focus:bg-white
                    focus:ring-4
                    focus:ring-sky-100
                    disabled:cursor-not-allowed
                    disabled:bg-gray-100
                  "
                />

                <p className="mt-2 text-xs text-gray-400">
                  Password must be at least 6 characters.
                </p>
              </div>

              {/* =================================================
                  RESET BUTTON
              ================================================== */}

              <button
                type="submit"
                disabled={loading}
                className="
                  group
                  flex
                  w-full
                  items-center
                  justify-center
                  rounded-xl
                  py-3.5
                  font-semibold
                  text-white
                  shadow-lg
                  transition-all
                  duration-200
                  hover:-translate-y-0.5
                  hover:shadow-xl
                  active:translate-y-0
                  disabled:cursor-not-allowed
                  disabled:opacity-60
                  disabled:hover:translate-y-0
                "
                style={{
                  background:
                    "linear-gradient(135deg, rgb(26, 58, 92), rgb(14, 165, 233))",
                }}
              >
                {loading ? (
                  <>
                    <FaSpinner className="mr-2 animate-spin" />
                    Resetting Password...
                  </>
                ) : (
                  <>
                    <FaLock className="transition-transform duration-200 group-hover:scale-110" />
                    Reset Password
                  </>
                )}
              </button>

              {/* =================================================
                  SECURITY
              ================================================== */}

              <div className="flex items-center justify-center gap-2 pt-1">
                <FaShieldAlt className="text-xs text-sky-500" />

                <p className="text-xs text-gray-400">
                  Your new password is securely protected.
                </p>
              </div>
            </form>
          )}

          {/* ====================================================
              LOGIN
          ==================================================== */}

          <div className="mt-7 border-t border-gray-100 pt-5 text-center">
            <span className="text-sm text-gray-500">
              Remember your password?{" "}
            </span>

            <Link
              to="/login"
              className="
                text-sm
                font-semibold
                text-sky-600
                transition-colors
                hover:text-blue-700
                hover:underline
              "
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>

      {/* ======================================================
          TOAST
      ====================================================== */}

      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default ResetPassword;

